import { Button, ButtonList, ClassName, ITableColumn, Label, Loader, Position, Table, TextAlign } from "@busybusy/webapp-react-ui";
import classNames from "classnames";
import DeleteConfirmationDialog from "containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog";
import HeaderText from "containers/settings/components/HeaderText/HeaderText";
import useOpenable from "hooks/utils/useOpenable";
import { t } from "i18next";
import { ReactNode, useState } from "react";
import MoreButton from "../buttons/MoreButton/MoreButton";
import EmptyState from "../state-templates/EmptyState/EmptyState";
import CertificationLinkDialog from "./CertificationLinkDialog/CertificationLinkDialog";
import { ICertificationLinkFormData } from "./CertificationLinkForm/CertificationLinkForm";
import './CertificationsDetails.scss';

export interface ICertificationDetailsProps {
  type: CertificationDetailsType;
  data: CertificationDetailsData[];
  loading: boolean;
  objectTitle: string;
  onDelete: (id: string) => void;
  onSubmit: (data: ICertificationLinkFormData) => void;
  className?: ClassName;
}

export interface CertificationDetailsData {
  id: string;
  certificationId: string;
  certification: {
    name: string;
    description?: string | null;
  }
}

export enum CertificationDetailsType {
  EQUIPMENT = 'equipment',
  PROJECT = 'project',
  COST_CODE = 'cost_code',
}

const CertificationDetails = (props: ICertificationDetailsProps) => {
  const { type, data, loading, objectTitle, onDelete, onSubmit, className } = props;

  const formOpenable = useOpenable();
  const deleteOpenable = useOpenable();

  const [selectedItem, setSelectedItem] = useState<CertificationDetailsData | undefined>();

  function handleNewRequirementClick() {
    formOpenable.open();
  }

  function handleDelete() {
    if (selectedItem) {
      onDelete(selectedItem.id);
    }
    setSelectedItem(undefined);
    deleteOpenable.close();
    formOpenable.close();
  }

  function handleSubmit(formData: ICertificationLinkFormData) {
    onSubmit(formData);
    formOpenable.close();
  }

  function handleLinkDialogClose() {
    setSelectedItem(undefined);
    formOpenable.close();
  }

  function renderColumns(): Array<ITableColumn<CertificationDetailsData>> {
    return [
      {
        cell: renderMoreButton,
        title: '',
        key: 'actions',
        sort: false,
        align: TextAlign.CENTER,
        size: '60px',
      },
      {
        cell: (row) => <Label>{row.certification.name}</Label>,
        title: t('Name'),
        key: 'name',
        sort: false,
        align: TextAlign.LEFT,
      },
      {
        cell: (row) => <Label>{row.certification.description}</Label>,
        title: t('Description'),
        key: 'description',
        sort: false,
        align: TextAlign.LEFT,
      },
    ];
  }

  function renderMoreButton(row: CertificationDetailsData) {
    const renderDropDownContent = (close: () => void) => {
      const handleDeleteClick = (e: React.MouseEvent) => {
        close();
        e.stopPropagation();
        e.preventDefault();

        setSelectedItem(row);
        deleteOpenable.open();
      };

      return (
        <ButtonList>
          <Button onClick={handleDeleteClick}>{t('Delete')}</Button>
        </ButtonList>
      );
    }

    return <MoreButton position={Position.BOTTOM_START} renderContent={renderDropDownContent} />;
  }

  function renderEmptyState(): ReactNode {
    return (
      !loading && (
        <EmptyState
          title={t('No Certifications')}
          subtitle={t('Use the New Requirement button above to get started.')}
        />
      )
    );
  }

  const classes = classNames('certification-details', className);

  return (
    <div className={classes}>
      <HeaderText title={t('Required Certifications')} className="title-label mb-6">
        <div className="details-label">
          {t(`Employees are required to have the following active certifications in order to ${type === CertificationDetailsType.EQUIPMENT ? 'operate the equipment' : type === CertificationDetailsType.PROJECT ? 'clock time to this project' : 'use the cost code'}.`)}
        </div>
      </HeaderText>
      <Button className="mb-6" type="secondary" rounded={true} onClick={handleNewRequirementClick}>
        {t('New Requirement')}
      </Button>
      <Table<CertificationDetailsData>
        cols={renderColumns()}
        data={data}
        strokeCols={false}
        lazyLoad={false}
        minWidth={`${renderColumns().length * 200}px`}
        header="sticky"
        emptyTemplate={renderEmptyState()}
      />
      <Loader isOpen={loading} overlay={true} />
      <DeleteConfirmationDialog
        isOpen={deleteOpenable.isOpen}
        onClose={deleteOpenable.close}
        deleteText={t('Delete')}
        onDelete={handleDelete}
        title={t('Delete Certification Requirement?')}
        showHeaderDivider={false}
      >
        {t(`If you delete this item it will be gone forever. Are you sure you want to proceed?`)}
      </DeleteConfirmationDialog>
      <CertificationLinkDialog
        subtitle={objectTitle}
        isOpen={formOpenable.isOpen}
        onClose={handleLinkDialogClose}
        onDelete={handleDelete}
        onSubmit={handleSubmit}
        existingCertificationIds={data.map((item) => item.certificationId)}
        certificationId={selectedItem?.certificationId}
      />
    </div>
  )
}

export default CertificationDetails;